import React, { useReducer } from 'react';
import { Divider, Panel, Stack } from 'rsuite';
import { FaFileDownload } from 'react-icons/fa';

import DownloadTemplate from '../components/DownloadTemplate';
import CardActionImportGuide from '../components/CardActionImportGuide';
import GeneratedDocumentQueuedModal from '../components/GeneratedDocument/GeneratedDocumentQueuedModal';
import GeneratedDocumentsModal from '../components/GeneratedDocument/GeneratedDocumentsModal';
import Guidelines from '../components/Guidelines';
import FileUploadCsv from '../components/FileUploadCsv';
import DownloadOfferDataReport from '../components/DownloadOfferDataReport';
import { ApiGroup } from '../api/model/ApiGroup';
import { GeneratedDocument, GeneratedDocumentType } from '../api/generateddocuments/generatedDocumentsApiTypes';
import { fullImportOffers } from '../api/products';
import { importReport } from '../api/listing';

interface Props {
  apiGroup: ApiGroup
}

interface State {
  showQueuedDocument: boolean;
  showGeneratedDocuments: boolean;
  document: GeneratedDocument | null;
}

const initialState: State = {
  showQueuedDocument: false,
  showGeneratedDocuments: false,
  document: null,
};

type DocumentSetAction = { type: 'document/set', payload: GeneratedDocument };
type GeneratedDocumentsShowAction = { type: 'generatedDocuments/show' };
type CloseAction = { type: 'close' };
type Action = DocumentSetAction | GeneratedDocumentsShowAction | CloseAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'document/set':
      return {
        ...state,
        showQueuedDocument: true,
        document: action.payload,
      };
    case 'generatedDocuments/show':
      return {
        ...state,
        showQueuedDocument: false,
        showGeneratedDocuments: true,
        document: null,
      };
    case 'close':
      return initialState;
    default:
      return state;
  }
}

function DownloadReportsButton({ onOpen }: { onOpen: () => void }) {
  return (
    <CardActionImportGuide onClick={() => onOpen()}>
      <FaFileDownload />
      <span>Download reports</span>
    </CardActionImportGuide>
  );
}

const templateFilenames = {
  [ApiGroup.KAUFLAND]: 'templateKauflandOffers.csv',
  [ApiGroup.ALLEGRO]: 'templateAllegroOffers.csv',
  [ApiGroup.ALZA]: '',
  [ApiGroup.MALL_GROUP]: '',
};

export default function ImportOffers({ apiGroup }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  function handleGenerated(document: GeneratedDocument): void {
    dispatch({ type: 'document/set', payload: document });
  }

  function handleShowDocuments(): void {
    dispatch({ type: 'generatedDocuments/show' });
  }

  function handleClose(): void {
    dispatch({ type: 'close' });
  }

  async function uploadFile(file: Blob, fileName: string) {
    const data = new FormData();
    data.append('attachment', file, fileName);

    let response;
    if (fileName.endsWith('.csv')) {
      response = await fullImportOffers(data, apiGroup)
        .then(res => res.data);
    } else {
      response = await importReport(data, apiGroup);
    }

    return response;
  }

  return (
    <Panel>
      <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
        <Guidelines />
        <DownloadTemplate filename={templateFilenames[apiGroup]} />
        <DownloadReportsButton onOpen={() => handleShowDocuments()} />
        <DownloadOfferDataReport
          apiGroup={apiGroup}
          onGenerated={(document) => handleGenerated(document)}
        />
      </Stack>
      <Divider />
      <h6>import</h6>
      <FileUploadCsv
        className="mt-5"
        getUploadResult={(f, fileName) => uploadFile(f, fileName)}
        accept={['.csv', '.xlsx']}
      />
      {state.showQueuedDocument && state.document && (
        <GeneratedDocumentQueuedModal
          open
          onClose={() => handleClose()}
          onShowDocuments={() => handleShowDocuments()}
          document={state.document}
        />
      )}
      <GeneratedDocumentsModal
        open={state.showGeneratedDocuments}
        onClose={() => handleClose()}
        type={GeneratedDocumentType.LISTING_REPORT_EXPORT}
      />
    </Panel>
  );
}
